import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import I18n from '../../../common/js/utils/I18n';
import Debug from '../../../common/js/utils/Debug';
import Config from '../../../common/js/utils/Config';
import PageSpinner from '../../../common/js/components/PageSpinner';
import withReducer from '../../../common/js/utils/WithReducer';
import { companyInfoReducer } from '../../../api/actions/companyApi';
import { languageReducer } from '../../../api/actions/languageSlice';

/**
 * CompanyPreviewLayout Component
 *
 * A layout component for displaying company preview information.
 * Handles locale initialization with preference for owner locale from companyInfo reducer.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render in the preview layout
 */
const CompanyPreviewLayout = ({ children }) => {
  // Get company data from Redux to check for owner.locale
  const companyData = useSelector((state) => (state.companyInfo ? state.companyInfo.companyData : {}));

  // Get language from Redux store
  const storeLanguage = useSelector((state) => (state.language ? state.language.selectedLanguage : null));

  // Determine language: prefer store language, then owner.locale from companyData, fallback to browser language
  const ownerLocale = companyData && companyData.owner && companyData.owner.locale;
  const browserLanguage = (navigator.language && navigator.language.split('-')[0]) || 'en';
  const language = storeLanguage || ownerLocale || browserLanguage;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initLocale();
  }, [language]); // Re-initialize when language changes (e.g., when companyData.owner.locale becomes available)

  const initLocale = () => {
    const basePath = Config.getAssetBase();
    setLoading(true);
    I18n(language, basePath).then(() => {
      Debug.log('[CompanyPreviewLayout] I18n loaded successfully');
      if (language !== 'en') {
        Debug.log('[CompanyPreviewLayout] Loading non-English language:', language);
        L10n.loadLanguage(language).then(() => {
          Debug.log('[CompanyPreviewLayout] Language loaded successfully:', language);
          document.title = Config.get('adminXProductName', true);
          setLoading(false);
        });
      } else {
        document.title = Config.get('adminXProductName', true);
        setLoading(false);
      }
    });
  };

  if (loading) {
    return <PageSpinner />;
  }

  return <div className='company-preview-layout'>{children}</div>;
};

CompanyPreviewLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Wrap with withReducer to inject both companyInfo and language reducers
export default withReducer({
  [companyInfoReducer.name]: companyInfoReducer.reducer,
  [languageReducer.name]: languageReducer.reducer,
})(CompanyPreviewLayout);
